<script setup>
import { computed, ref, watch } from 'vue'
import { useCareVisitDashboardFilters } from '@/composables/useCareVisitDashboardFilters'
import ArrivalsByHourChart from '@/views/care-visit/dashboard/ArrivalsByHourChart.vue'
import DailyCheckInOutChart from '@/views/care-visit/dashboard/DailyCheckInOutChart.vue'
import DashboardFilterBar from '@/views/care-visit/dashboard/DashboardFilterBar.vue'
import KioskHealthTable from '@/views/care-visit/dashboard/KioskHealthTable.vue'
import ManualPassesChart from '@/views/care-visit/dashboard/ManualPassesChart.vue'
import MissedCheckoutChart from '@/views/care-visit/dashboard/MissedCheckoutChart.vue'
import OffWindowByFloorChart from '@/views/care-visit/dashboard/OffWindowByFloorChart.vue'
import OverstayByFloorChart from '@/views/care-visit/dashboard/OverstayByFloorChart.vue'

definePage({
  meta: {
    name: 'Dashboard',
    subject: 'care_visit.menu',
    action: 'dashboard',
  },
})

// Shared with the Visitor Traffic page — see the composable for the defaults
// and the debounce rationale.
const {
  selectedFloorIds,
  selectedVisitorType,
  dateRange,
  datePickerConfig,
  floors,
  visitorTypes,
  filters,
  debouncedFilters,
} = useCareVisitDashboardFilters()

// Clicking a floor in the compliance bar focuses the hourly profile on it.
const focusedFloorId = ref(null)

// A focused floor is only meaningful for the current filter set: if the
// floor filter, visitor type or date range changes, the focused bar may no
// longer exist (or may belong to a different comparison), so any change to
// the filter inputs clears it rather than leaving a stale, unclearable focus
// behind.
watch(filters, () => {
  focusedFloorId.value = null
}, { deep: true })

const hourFilters = computed(() => ({
  ...debouncedFilters.value,
  floorIds: focusedFloorId.value ? [focusedFloorId.value] : debouncedFilters.value.floorIds,
}))

const focusedFloorName = computed(() => {
  if (!focusedFloorId.value) return null

  return floors.value.find(f => f.id === focusedFloorId.value)?.name ?? null
})
</script>

<template>
  <div>
    <div class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center gap-4 mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold">
          Dashboard
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-0">
          Visitor flow, check-out compliance and kiosk health
        </p>
      </div>

      <DashboardFilterBar
        v-model:floor-ids="selectedFloorIds"
        v-model:visitor-type="selectedVisitorType"
        v-model:date-range="dateRange"
        :floors="floors"
        :visitor-types="visitorTypes"
        :date-picker-config="datePickerConfig"
      />
    </div>

    <VRow>
      <VCol cols="12">
        <DailyCheckInOutChart :filters="debouncedFilters" />
      </VCol>

      <VCol cols="12">
        <OverstayByFloorChart :filters="debouncedFilters" />
      </VCol>

      <!--
        The two floor-ranked compliance cards side by side. Clicking a floor
        bar in Off-window narrows the hourly profile below it.
      -->
      <VCol
        cols="12"
        md="6"
      >
        <MissedCheckoutChart :filters="debouncedFilters" />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <OffWindowByFloorChart
          :filters="debouncedFilters"
          :focused-floor-id="focusedFloorId"
          @select-floor="id => (focusedFloorId = focusedFloorId === id ? null : id)"
        />
      </VCol>

      <VCol cols="12">
        <ArrivalsByHourChart
          :filters="hourFilters"
          :focused-floor-name="focusedFloorName"
          @clear-focus="focusedFloorId = null"
        />
      </VCol>

      <VCol cols="12">
        <ManualPassesChart :filters="debouncedFilters" />
      </VCol>

      <VCol cols="12">
        <KioskHealthTable :filters="debouncedFilters" />
      </VCol>
    </VRow>
  </div>
</template>
